
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    /*
| Developed by Dirupt
| Filename : create-user.tsx
| Author : Philippe DESPLATS (philippe@dirupt.com)
*/
import { Box, FormHelperText, Typography } from '@mui/material';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import React from 'react';
import * as Yup from 'yup';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { isAxiosError } from 'axios';
import { toast } from 'sonner';
import { TextField } from 'formik-mui';
import { useSearchParams } from 'next/navigation';
import { IN_DEV } from '@/config/common.config';
import { createAuthServerSideProps } from '@/components/ssr/createServerSideProps';
import { SensklePageFactory } from '@/components/ssr/SensklePage';
import { UrlsConfig } from '@/config/urls.config';
import { Link } from '@/components/common/MLink';
import { MButton } from '@/components/common/MButton';
import { MDarkModeSwitch } from '@/components/common/MDarkModeSwitch';
import { UserCreateDto } from '@/services/api/modules/user/user.dto';
import { UserEndpoint } from '@/services/api/modules/user/user.endpoint';
import { EmailField, PasswordField, YupPasswordValidation } from '@/components/formik';
import { ErrorCodes } from '@/services/api/common.contract';
import { AxiosErrorHandler } from '@/services/api/axios-error-handler';
import { LangSwitch } from '@/components/common/LangSwitch';
/*
|--------------------------------------------------------------------------
| Page
|--------------------------------------------------------------------------
*/
const CreateAccountPage = SensklePageFactory(() => {
    const { t } = useTranslation();
    const router = useRouter();
    const params = useSearchParams();
    const code = params.get('code');
    const email = params.get('email');
    // Form
    // ----------------------------------------------------------------------------
    const initialValues: UserCreateDto = React.useMemo(() => {
        return {
            email: email || '',
            password: IN_DEV ? 'DGsrgqr5@B@JETQ&DRgWoSW3' : '',
            redeemCode: code || '',
        };
    }, [code, email]);
    const validationSchema = React.useMemo(() => {
        return Yup.object().shape({
            email: Yup.string().email().required(),
            // Check if password is strong enough (8 characters, 1 uppercase, 1 lowercase, 1 number, 1 special character)
            password: YupPasswordValidation(t),
            redeemCode: Yup.string().optional(),
        });
    }, [t]);
    // Methods
    // ----------------------------------------------------------------------------
    const handleRegister = async (values: UserCreateDto, formikHelpers: FormikHelpers<UserCreateDto>) => {
        try {
            await UserEndpoint.create(values);
            // if (data.data.beta) {
            void router.replace(UrlsConfig.auth.verify(values.email));
            // void router.replace(UrlsConfig.account.onboarding)
            // } else {
            // 	void router.replace(UrlsConfig.auth.betaRestricted)
            // }
        }
        catch (error) {
            AxiosErrorHandler.handle(t, error, () => {
                if (isAxiosError(error)) {
                    switch (error?.response?.status) {
                        case 409:
                            formikHelpers.setFieldError('email', t('auth/create-account:errors.emailAlreadyInUse'));
                            break;
                        case 400:
                            // Check if code is in data
                            if (error.response.data && error.response.data.code) {
                                if (error.response.data.code === ErrorCodes.E_REDEEM_CODE_INVALID) {
                                    formikHelpers.setFieldError('redeemCode', t('auth/create-account:redeemCode.errors.invalidCode'));
                                }
                                else if (error.response.data.code === ErrorCodes.E_REDEEM_CODE_ALREADY_USED) {
                                    formikHelpers.setFieldError('redeemCode', t('auth/create-account:redeemCode.errors.alreadyUsed'));
                                }
                                else if (error.response.data.code === ErrorCodes.E_REDEEM_CODE_ASSOCIATED_TO_OTHER_USER) {
                                    formikHelpers.setFieldError('redeemCode', t('auth/create-account:redeemCode.errors.associatedToAnotherUser'));
                                }
                            }
                            break;
                        case 403:
                            // Check if code is in data
                            if (error.response.data && error.response.data.code) {
                                if (error.response.data.code === ErrorCodes.E_REDEEM_CODE_INVALID) {
                                    formikHelpers.setFieldError('redeemCode', t('auth/create-account:redeemCode.errors.invalidCode'));
                                }
                                else if (error.response.data.code === ErrorCodes.E_REDEEM_CODE_ALREADY_USED) {
                                    formikHelpers.setFieldError('redeemCode', t('auth/create-account:redeemCode.errors.alreadyUsed'));
                                }
                            }
                            break;
                        default:
                            toast.error(t('common:errors.errorOccurred'));
                    }
                }
                else {
                    toast.error(t('common:errors.errorOccurred'));
                }
                console.log(error);
            });
        }
        finally {
            formikHelpers.setSubmitting(false);
        }
    };
    // Render
    // ----------------------------------------------------------------------------
    return (<Formik initialValues={initialValues} onSubmit={handleRegister} validationSchema={validationSchema} enableReinitialize>
			{(formikProps) => (<Form>
					<Box display={'flex'} flexDirection={'column'} justifyContent={'center'} gap={4} width={'100%'}>
						<Box textAlign={'center'}>
							<Typography variant={'h1'}>{t('auth/create-account:title')}</Typography>
							<Typography>{t('auth/create-account:subTitle')}</Typography>
						</Box>
						<Box display={'flex'} flexDirection={'column'} gap={2}>
							<EmailField />
							<Box>
								<PasswordField />
								<FormHelperText sx={{ marginLeft: '14px', color: (theme) => theme.palette.text.disabled }}>
									{t('common:form.password.policy')}
								</FormHelperText>
							</Box>
							<Field component={TextField} name={'redeemCode'} label={t('auth/create-account:redeemCode.label')} helperText={t('auth/create-account:redeemCode.helperText')} fullWidth/>
						</Box>
						<Box display={'flex'} flexDirection={'column'} gap={1}>
							<MButton variant="contained" type="submit">
								{t(`auth/create-account:registerButton.${formikProps.values.redeemCode || formikProps.values.redeemCode !== ''
                ? 'withCode'
                : 'withoutCode'}`)}
							</MButton>
						</Box>
						<Box display={'flex'} flexDirection={'column'} justifyContent={'center'} alignItems={'center'} gap={0.5}>
							<Typography variant={'body2'}>{t('auth/create-account:alreadyRegistered')}</Typography>
							<MButton variant={'text'} size={'large'} component={Link} href={UrlsConfig.auth.login}>
								{t('auth/create-account:login')}
							</MButton>
						</Box>
						<Box display={'flex'} justifyContent={'space-between'} gap={2}>
							<MDarkModeSwitch />
							<LangSwitch />
						</Box>
					</Box>
				</Form>)}
		</Formik>);
});
/*
|--------------------------------------------------------------------------
| Configurations
|--------------------------------------------------------------------------
*/
CreateAccountPage.layout = 'auth';
export default CreateAccountPage;
/*
|--------------------------------------------------------------------------
| Server Side Props
|--------------------------------------------------------------------------
*/
const getServerSideProps = createAuthServerSideProps();

    async function __Next_Translate__getServerSideProps__1a09ebd484c__(ctx) {
      const res = await getServerSideProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/auth/create-account',
            loaderName: 'getServerSideProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getServerSideProps__1a09ebd484c__ as getServerSideProps }
  